'use client';

import { observer } from 'mobx-react-lite';
import { useCheckoutContext } from '@instamenu/context-checkout';
import { Text, Box, Flex } from '@instamenu/ui-system-next';
import { TipButton } from '../tip-button/tip-button';
import { formatCurrency } from '@instamenu/formatters';
import { palette, themeVars } from '@instamenu/ui-vanilla-extract';
import { Fulfillment } from '@instamenu/data-access';

export const OrderTotals = observer(() => {
  const order = useCheckoutContext()?.order;
  if (!order) return null;

  const filterStyle = order.loading ? { filter: 'blur(3px)' } : null;

  return (
    <Box css={{ paddingTop: 20 }}>
      <Flex style={{ alignItems: 'center', justifyContent: 'space-between', paddingBottom: 16 }}>
        <Text variant="caption1" css={{ color: palette.gray400 }}>
          Subtotal
        </Text>
        <Text variant="numeric" css={{ ...filterStyle, color: palette.gray400 }}>
          {formatCurrency(order.netOrderItemsTotal)}
        </Text>
      </Flex>
      {order.discount?.id ? (
        <Flex style={{ alignItems: 'center', justifyContent: 'space-between', paddingBottom: 20 }}>
          <Text css={{ color: themeVars.color.brand }} variant="caption1">
            {order.discount?.name}
          </Text>
          <Text css={{ ...filterStyle, color: themeVars.color.brand }} variant="caption1">
            -{order.discountAmount && formatCurrency(order.discountAmount)}
          </Text>
        </Flex>
      ) : null}
      {order.orderItems
        .filter((oi) => oi.loyaltyComped)
        .map((oi) => (
          <Flex
            key={`loyalty-${oi.id}`}
            style={{ alignItems: 'center', justifyContent: 'space-between', paddingBottom: 16 }}
          >
            <div>
              <Text variant="caption1" css={{ color: '#166534' }}>
                Free item — {oi.item.name}
              </Text>
              {!oi.loyaltyCompedModifiers && (
                <Text variant="caption2" css={{ color: '#6B6868', fontSize: 11 }}>
                  Add-ons are not included
                </Text>
              )}
            </div>
            <Text variant="numeric" css={{ ...filterStyle, color: '#166534' }}>
              {/* `unitPrice` and `item.price` are per-unit values — multiply by
                  `quantity` so the discount line reflects the full line total
                  that was comped (matches what `order.total` actually subtracts). */}
              -
              {formatCurrency(
                (oi.loyaltyCompedModifiers ? oi.unitPrice : oi.item.price) * oi.quantity,
              )}
            </Text>
          </Flex>
        ))}
      {order.orderSurcharges.map((orderSurcharge) => (
        <Flex
          key={orderSurcharge.id}
          style={{ alignItems: 'center', justifyContent: 'space-between', paddingBottom: 16 }}
        >
          <Flex style={{ alignItems: 'center' }}>
            <Text variant="caption1" css={{ color: palette.gray400 }}>
              {orderSurcharge.surcharge.nameWithValue}
            </Text>
          </Flex>
          <Text variant="numeric" css={{ ...filterStyle, color: palette.gray400 }}>
            {formatCurrency(orderSurcharge.amount)}
          </Text>
        </Flex>
      ))}
      <Flex style={{ alignItems: 'center', justifyContent: 'space-between', paddingBottom: 16 }}>
        <Flex style={{ alignItems: 'center' }}>
          <Text variant="caption1" css={{ color: palette.gray400 }}>
            {order.unincludedTax === 0 ? 'Fees' : 'Taxes & fees'}
          </Text>
        </Flex>
        <Text variant="numeric" css={{ ...filterStyle, color: palette.gray400 }}>
          {formatCurrency(order.processingFee + (order.unincludedTax ?? 0))}
        </Text>
      </Flex>
      <Flex style={{ alignItems: 'center', justifyContent: 'space-between', paddingBottom: 16 }}>
        <Flex style={{ alignItems: 'center' }}>
          <Text variant="caption1" css={{ color: palette.gray400 }}>
            {order.fulfillment === Fulfillment.Delivery ? 'Driver tip' : 'Tip'}
          </Text>
          <TipButton />
        </Flex>
        <Text variant="numeric" css={{ ...filterStyle, color: palette.gray400 }}>
          {formatCurrency(
            order.fulfillment === Fulfillment.Delivery ? order.deliveryTip : order.tip,
          )}
        </Text>
      </Flex>
      {order.fulfillment === Fulfillment.Delivery ? (
        <Flex style={{ alignItems: 'center', justifyContent: 'space-between', paddingBottom: 16 }}>
          <Text variant="caption1" css={{ color: palette.gray400 }}>
            Delivery
          </Text>
          <Text variant="numeric" css={{ ...filterStyle, color: palette.gray400 }}>
            {formatCurrency(order.deliveryFee)}
          </Text>
        </Flex>
      ) : null}
      <Flex style={{ alignItems: 'center', justifyContent: 'space-between', paddingBottom: 20 }}>
        <Text variant="body1">Total</Text>
        <Text css={{ ...filterStyle }} variant="numeric">
          {formatCurrency(order.total)}
        </Text>
      </Flex>
    </Box>
  );
});
